import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ConsultationModal = ({ open, onClose }) => {
  const [formData, setFormData] = useState({
    scheduled_date: '',
    scheduled_time: '',
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [consultations, setConsultations] = useState([]);

  useEffect(() => {
    if (open) {
      fetchConsultations();
    }
  }, [open]);

  const fetchConsultations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/consultation/list`);
      setConsultations(response.data);
    } catch (error) {
      console.error('Failed to fetch consultations:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/consultation/schedule`, formData);
      toast.success('Consultation scheduled successfully!');
      setFormData({ scheduled_date: '', scheduled_time: '', description: '' });
      fetchConsultations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule consultation');
    } finally {
      setLoading(false);
    }
  };

  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading font-semibold text-2xl">Schedule Consultation</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="date">Preferred Date</Label>
              <Input
                id="date"
                type="date"
                min={getMinDate()}
                value={formData.scheduled_date}
                onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                required
                className="rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="time">Preferred Time</Label>
              <Input
                id="time"
                type="time"
                value={formData.scheduled_time}
                onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
                required
                className="rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description / Reason for Call</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Please describe what you'd like to discuss..."
                required
                rows={4}
                className="rounded-xl"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-primary hover:bg-primary/90"
            >
              {loading ? 'Scheduling...' : 'Schedule Consultation'}
            </Button>
          </form>

          {consultations.length > 0 && (
            <div className="mt-6">
              <h3 className="font-heading font-semibold text-lg mb-3">Your Consultations</h3>
              <div className="space-y-2">
                {consultations.map((consultation) => (
                  <div key={consultation.id} className="p-4 rounded-xl border bg-muted">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{consultation.scheduled_date} at {consultation.scheduled_time}</p>
                        <p className="text-sm text-muted-foreground mt-1">{consultation.description}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        consultation.status === 'confirmed' ? 'bg-success/20 text-success' :
                        consultation.status === 'completed' ? 'bg-primary/20 text-primary' :
                        'bg-warning/20 text-warning'
                      }`}>
                        {consultation.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConsultationModal;